import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CustomerGroupSelect } from "@/components/shared/CustomerGroupSelect";
import { FieldError } from "@/lib/customerRegistrationError";
import { formatCpf, formatPhone } from "@/lib/formatters";
import { CPF_ALREADY_REGISTERED_MESSAGE } from "@/lib/physicalClientError";
import {
  createPhysicalClientSchema,
  CreatePhysicalClientFormValues,
  updatePhysicalClientSchema,
} from "./physicalClientFormSchema";

export type PhysicalClientFormValues = CreatePhysicalClientFormValues & { comment?: string };

interface PhysicalClientFormProps {
  /** create: com CPF e checagem de duplicidade. edit: sem CPF (imutável), com observação para o histórico. */
  mode: "create" | "edit";
  defaultValues: PhysicalClientFormValues;
  isSubmitting?: boolean;
  /** Erros de campo devolvidos pela API (400), exibidos junto do campo. */
  serverErrors?: FieldError[];
  /** Cadastro existente com o CPF digitado: aviso inline e salvar bloqueado. */
  duplicate?: { physicalClientId?: number | null } | null;
  isCheckingCpf?: boolean;
  /** Ao sair do campo CPF (o valor digitado, com máscara). */
  onCpfBlur?: (cpf: string) => void;
  /** CPF alterado: a checagem anterior deixa de valer. */
  onCpfChange?: () => void;
  onSubmit: (values: PhysicalClientFormValues) => void | Promise<void>;
  onCancel?: () => void;
  /** Avisa quando o formulário passa a ter (ou deixa de ter) alterações não salvas. */
  onDirtyChange?: (dirty: boolean) => void;
  submitLabel: string;
}

/** Formulário de cadastro e de edição do cliente pessoa física. */
export function PhysicalClientForm({
  mode,
  defaultValues,
  isSubmitting = false,
  serverErrors = [],
  duplicate,
  isCheckingCpf = false,
  onCpfBlur,
  onCpfChange,
  onSubmit,
  onCancel,
  onDirtyChange,
  submitLabel,
}: PhysicalClientFormProps) {
  const form = useForm<PhysicalClientFormValues>({
    resolver: zodResolver(mode === "create" ? createPhysicalClientSchema : updatePhysicalClientSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  useEffect(() => {
    for (const error of serverErrors) {
      if (error.field in defaultValues) {
        form.setError(error.field as keyof PhysicalClientFormValues, { message: error.message });
      }
    }
  }, [serverErrors, defaultValues, form]);

  const { isDirty } = form.formState;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const blocked = mode === "create" && !!duplicate;
  // Na edição, salvar sem mudança só criaria uma linha "Sem alteração de dados" no histórico.
  const unchanged = mode === "edit" && !isDirty;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {mode === "create" && (
          <FormField
            control={form.control}
            name="cpf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CPF</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="000.000.000-00"
                    maxLength={14}
                    onChange={(event) => {
                      field.onChange(formatCpf(event.target.value));
                      onCpfChange?.();
                    }}
                    onBlur={() => {
                      field.onBlur();
                      onCpfBlur?.(field.value);
                    }}
                  />
                </FormControl>
                {isCheckingCpf && (
                  <FormDescription className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    Verificando se o CPF já está cadastrado...
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {blocked && (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center gap-x-2">
              {CPF_ALREADY_REGISTERED_MESSAGE}
              {duplicate?.physicalClientId && (
                <Link className="font-medium underline" to={`/backoffice/clientes/pf/${duplicate.physicalClientId}`}>
                  Ver o cadastro existente
                </Link>
              )}
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome completo</FormLabel>
              <FormControl>
                <Input {...field} autoComplete="off" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input {...field} type="email" autoComplete="off" />
                </FormControl>
                <FormDescription>Informe um e-mail ou um celular.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="mobilePhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Celular</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="tel"
                    autoComplete="off"
                    placeholder="(00) 00000-0000"
                    onChange={(event) => field.onChange(formatPhone(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone (opcional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="tel"
                    autoComplete="off"
                    placeholder="(00) 0000-0000"
                    onChange={(event) => field.onChange(formatPhone(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="erpCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Código no ERP (opcional)</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="off" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="customerGroupId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Grupo de cliente (opcional)</FormLabel>
              <FormControl>
                <CustomerGroupSelect
                  value={field.value}
                  onChange={field.onChange}
                  onClear={() => field.onChange(null)}
                  clearLabel="Sem grupo"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {mode === "edit" && (
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observação (opcional)</FormLabel>
                <FormControl>
                  <Textarea {...field} rows={2} />
                </FormControl>
                <FormDescription>Fica registrada no histórico do cliente.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          {mode === "edit" && isDirty && (
            <>
              <p className="mr-auto text-sm text-muted-foreground" role="status">
                Alterações não salvas.
              </p>
              <Button type="button" variant="outline" onClick={() => form.reset()} disabled={isSubmitting}>
                Descartar alterações
              </Button>
            </>
          )}
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting || blocked || unchanged}>
            {isSubmitting ? "Salvando..." : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
